import { type CSSProperties, type ReactNode } from 'react';
import { Check, Minus, Sparkles, X } from 'lucide-react';
import { supabase } from '../supabase';
import { isBillingConfigured, openCheckout, PRO_PRICE, PRO_PERIOD } from '../billing';
import { FREE_LIMITS } from '../entitlements';

/**
 * Pricing / feature-comparison paywall. Opened via the `skema:open-upgrade`
 * event (account menu, or when a free user hits a limit like the subsystem cap)
 * and sends the user to the Lemon Squeezy checkout. Numbers come from
 * FREE_LIMITS so the table can't drift from what's actually enforced.
 */
const ROWS: { label: string; free: ReactNode; pro: ReactNode }[] = [
  { label: 'Block diagrams, schematics & harnesses', free: true, pro: true },
  { label: 'PNG export', free: true, pro: true },
  { label: 'Cloud-saved projects', free: String(FREE_LIMITS.cloudProjects), pro: 'Unlimited' },
  { label: 'Subsystems per project', free: String(FREE_LIMITS.subsystems), pro: 'Unlimited' },
  { label: 'Reference photos', free: false, pro: true },
  { label: 'PDF drawing-set export', free: false, pro: true },
  { label: 'Excel export (wiring, BOM, netlist)', free: false, pro: true },
];

export function UpgradeModal({ onClose }: { onClose: () => void }) {
  const upgrade = async () => {
    const { data } = (await supabase?.auth.getSession()) ?? { data: { session: null } };
    openCheckout(data.session);
  };

  return (
    <div style={overlay} onMouseDown={onClose}>
      <div style={card} onMouseDown={(e) => e.stopPropagation()}>
        <button style={closeBtn} onClick={onClose} title="Close">
          <X size={16} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={18} style={{ color: 'var(--accent)' }} />
          <h2 style={h2}>Upgrade to Skematiq Pro</h2>
        </div>
        <p style={sub}>
          Free is great for simple builds. Pro unlocks the scale and the
          professional deliverables real projects need.
        </p>

        <div style={table}>
          <div style={{ ...headRow }}>
            <div style={featureCell}>&nbsp;</div>
            <div style={colHead}>Free</div>
            <div style={{ ...colHead, color: 'var(--accent)' }}>Pro</div>
          </div>
          {ROWS.map((r) => (
            <div key={r.label} style={row}>
              <div style={featureCell}>{r.label}</div>
              <div style={valueCell}>{renderCell(r.free)}</div>
              <div style={valueCell}>{renderCell(r.pro)}</div>
            </div>
          ))}
        </div>

        <div style={priceRow}>
          <span style={{ fontSize: 22, fontWeight: 800 }}>{PRO_PRICE}</span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>/ {PRO_PERIOD}</span>
        </div>

        {isBillingConfigured ? (
          <button style={cta} onClick={() => void upgrade()}>
            <Sparkles size={15} /> Upgrade to Pro
          </button>
        ) : (
          <div style={notConfigured}>Checkout isn’t configured yet.</div>
        )}
        <div style={foot}>Cancel anytime · secure checkout by Lemon Squeezy</div>
      </div>
    </div>
  );
}

function renderCell(v: ReactNode) {
  if (v === true) return <Check size={16} style={{ color: 'var(--accent)' }} />;
  if (v === false) return <Minus size={15} style={{ color: 'var(--text-dim)' }} />;
  return <span style={{ fontSize: 12.5, fontWeight: 600 }}>{v}</span>;
}

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  padding: 16,
};
const card: CSSProperties = {
  position: 'relative',
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border-strong, var(--panel-border))',
  borderTop: '3px solid var(--accent)',
  borderRadius: 'var(--radius)',
  padding: 24,
  width: 460,
  maxWidth: '100%',
  maxHeight: 'calc(100vh - 40px)',
  overflowY: 'auto',
  boxShadow: 'var(--shadow-lg)',
};
const closeBtn: CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 12,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--text-muted)',
  cursor: 'pointer',
};
const h2: CSSProperties = { fontSize: 18, fontWeight: 800, margin: 0 };
const sub: CSSProperties = {
  fontSize: 13,
  color: 'var(--text-muted)',
  lineHeight: 1.5,
  margin: '8px 0 16px',
};
const table: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  overflow: 'hidden',
};
const headRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 72px 72px',
  alignItems: 'center',
  background: 'var(--panel-alt, var(--panel))',
  borderBottom: '1px solid var(--panel-border)',
};
const row: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 72px 72px',
  alignItems: 'center',
  borderBottom: '1px solid var(--panel-border)',
};
const featureCell: CSSProperties = { fontSize: 12.5, padding: '9px 12px', color: 'var(--text)' };
const colHead: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  textAlign: 'center',
  padding: '9px 6px',
  color: 'var(--text-muted)',
};
const valueCell: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '9px 6px',
};
const priceRow: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
  justifyContent: 'center',
  margin: '18px 0 12px',
};
const cta: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 800,
  padding: '11px 14px',
  cursor: 'pointer',
};
const notConfigured: CSSProperties = {
  textAlign: 'center',
  fontSize: 12.5,
  color: 'var(--text-muted)',
  padding: '10px 0',
};
const foot: CSSProperties = {
  textAlign: 'center',
  fontSize: 11,
  color: 'var(--text-dim)',
  marginTop: 10,
};
